// Shared client-side trade math used by TradeDetail and Journal.

export function computeEstimatedPnl(trade) {
  const entry = trade?.entry_price;
  const shares = trade?.shares;
  const direction = trade?.direction || "long";
  const levels = (trade?.take_profit_levels || []).filter((lv) => lv?.price != null);
  if (entry == null || !shares || levels.length === 0) return null;

  const withSize = levels.map((lv) => ({
    price: Number(lv.price),
    size_pct: lv.size_pct != null ? Number(lv.size_pct) : null,
  }));
  const knownTotal = withSize.filter((lv) => lv.size_pct != null).reduce((a, lv) => a + lv.size_pct, 0);
  const unknownCount = withSize.filter((lv) => lv.size_pct == null).length;
  const remaining = Math.max(0, 100 - knownTotal);
  const evenShare = unknownCount > 0 ? remaining / unknownCount : 0;

  let pnl = 0;
  let allocated = 0;
  for (const lv of withSize) {
    const size = lv.size_pct != null ? lv.size_pct : evenShare;
    if (size <= 0) continue;
    const sharesAtLevel = shares * (size / 100);
    const per = direction === "long" ? lv.price - entry : entry - lv.price;
    pnl += per * sharesAtLevel;
    allocated += size;
  }
  const account = trade.account_size;
  const pnlPct = account ? (pnl / account) * 100 : null;
  const costBasis = entry * shares;
  const pnlPctOfTrade = costBasis > 0 ? (pnl / costBasis) * 100 : null;
  return { pnl, pnl_pct: pnlPct, pnl_pct_of_trade: pnlPctOfTrade, allocated_pct: allocated };
}

// Max loss in dollars (positive value). Returns null if not computable.
export function computeMaxLoss(trade) {
  const entry = trade?.entry_price;
  const stop = trade?.stop_loss;
  const shares = trade?.shares;
  if (entry == null || stop == null || !shares) return null;
  const dir = trade?.direction || "long";
  const perShare = dir === "long" ? (entry - stop) : (stop - entry);
  const loss = perShare * shares;
  return loss > 0 ? loss : null;
}

// R:R = Est. Profit ÷ |Est. Max Loss|. Null if profit ≤ 0 or max loss not computable.
export function computeRR(trade) {
  const est = computeEstimatedPnl(trade);
  const maxLoss = computeMaxLoss(trade);
  if (!est || est.pnl <= 0 || !maxLoss) return null;
  return est.pnl / maxLoss;
}
